import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ProjectDurationData, ProjectDurationResponse, ChartDataPoint } from '../models/project-duration.models';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ProjectDurationService {
  private apiUrl = `${environment.main_url}/agilelegal/reports/tasks/project-duration`;
  private organizationsUrl = `${environment.main_url}/agilelegal/organizations/names`;
  private mattersUrl = `${environment.main_url}/agilelegal/matters/names`;
  private projectsUrl = `${environment.main_url}/agilelegal/projects/names`;
  private projectTypesUrl = `${environment.main_url}${environment.projectTypesList}`;
  private lpcUsersUrl = `${environment.main_url}${environment.lpcUsersList}`;

  constructor(private http: HttpClient) {}

  /**
   * Fetches project duration data from the API with optional filters
   */
  getProjectDurationData(filters?: any): Observable<ProjectDurationResponse> {
    let params = new HttpParams();
    
    // Add filter parameters if provided with correct field names
    if (filters) {
      if (filters.organization) {
        params = params.set('organization', filters.organization);
      }
      if (filters.matter) {
        params = params.set('matter', filters.matter);
      }
      if (filters.project) {
        params = params.set('projectName', filters.project);
      }
      if (filters.lpc) {
        params = params.set('lpcMainEmail', filters.lpc);
      }
      if (filters.projectType) {
        params = params.set('projectTypeCode', filters.projectType);
      }
    }
    
    return this.http.get<ProjectDurationResponse>(this.apiUrl, { params });
  }

  /**
   * Fetches organization names for the filter dropdown
   */
  getOrganizationNames(): Observable<any> {
    return this.http.get<any>(this.organizationsUrl)
      .pipe(
        map(response => {
          if (response && response.data && Array.isArray(response.data)) {
            return {
              data: response.data.map((name: string) => ({
                id: name,
                name: name
              })),
              message: response.message,
              customValues: response.customValues
            };
          }
          return response;
        }),
        catchError(error => {
          console.error('Error fetching organization names:', error);
          return of({ data: [], success: false, message: 'Failed to fetch organizations' });
        })
      );
  }

  /**
   * Fetches matter names for the filter dropdown
   * @param organization Optional organization name to filter matters
   */
  getMatterNames(organization?: string): Observable<any> {
    let url = this.mattersUrl;
    
    // If organization is provided, add it as a query parameter
    if (organization) {
      url = `${this.mattersUrl}?organization=${encodeURIComponent(organization)}`;
    }
    
    return this.http.get<any>(url)
      .pipe(
        map(response => {
          if (response && response.data && Array.isArray(response.data)) {
            // Handle both string array and object array formats
            if (response.data.length > 0 && typeof response.data[0] === 'string') {
              return {
                data: response.data.map((name: string) => ({
                  id: name,
                  name: name
                })),
                message: response.message,
                customValues: response.customValues
              };
            } else {
              return {
                data: response.data.map((matter: any) => ({
                  id: matter.name || matter.id,
                  name: matter.name
                })),
                message: response.message,
                customValues: response.customValues
              };
            }
          }
          return response;
        }),
        catchError(error => {
          console.error('Error fetching matter names:', error);
          return of({ data: [], success: false, message: 'Failed to fetch matters' });
        })
      );
  }

  /**
   * Fetches all projects for the filter dropdown
   * @param matter Optional matter name to filter projects
   */
  getAllProjects(matter?: string): Observable<any> {
    let url = this.projectsUrl;
    
    // If matter is provided, add it as a query parameter
    if (matter) {
      url = `${this.projectsUrl}?matter=${encodeURIComponent(matter)}`;
    }
    
    return this.http.get<any>(url)
      .pipe(
        map(response => {
          if (response && response.data && Array.isArray(response.data)) {
            // Handle string array format (project names)
            return {
              data: response.data.map((projectName: string) => ({
                id: projectName,
                name: projectName
              })),
              message: response.message,
              customValues: response.customValues
            };
          }
          return response;
        }),
        catchError(error => {
          console.error('Error fetching projects:', error);
          return of({ data: [], success: false, message: 'Failed to fetch projects' });
        })
      );
  }

  /**
   * Fetches project types for the filter dropdown
   */
  getProjectTypes(): Observable<any> {
    return this.http.get<any>(this.projectTypesUrl)
      .pipe(
        map(response => {
          if (response && response.data && Array.isArray(response.data)) {
            return {
              data: response.data.map((type: any) => ({
                id: type.code, // Using code as the ID value for the payload
                name: type.description // Using description as the display name
              })),
              message: response.message,
              customValues: response.customValues
            };
          }
          return response;
        }),
        catchError(error => {
          console.error('Error fetching project types:', error);
          return of({ data: [], success: false, message: 'Failed to fetch project types' });
        })
      );
  }

  /**
   * Fetches LPC users for the filter dropdown
   */
  getLPCUsers(payload: any = {}): Observable<any> {
    return this.http.post<any>(this.lpcUsersUrl, payload)
      .pipe(
        map(response => {
          if (response && response.data && Array.isArray(response.data)) {
            return {
              data: response.data.map((user: any) => {
                // Combine firstName, middleName, and lastName
                const nameParts = [
                  user.firstName || '',
                  user.middleName || '',
                  user.lastName || ''
                ].filter(part => part.trim() !== '');
                
                const fullName = nameParts.join(' ').trim();
                
                return {
                  id: user.mainEmail,
                  name: fullName || user.mainEmail // Fallback to email if no name
                };
              }),
              message: response.message,
              customValues: response.customValues
            };
          }
          return response;
        }),
        catchError(error => {
          console.error('Error fetching LPC users:', error);
          return of({ data: [], success: false, message: 'Failed to fetch LPC users' });
        })
      );
  }

  /**
   * Transforms the API response into chart data format
   * Groups data according to requirements:
   * - Graph 1: Open/In Progress projects (NOT Completed OR Rejected)
   * - Graph 2: Completed projects
   */
  getChartData(filters?: any): Observable<ChartDataPoint[]> {
    return this.getProjectDurationData(filters).pipe(
      map(response => {
        // Filter out any items with null or undefined values
        const validData = response.data.filter(item => 
          item.projCategory && 
          item.estimatedHours != null && 
          item.actualHours != null
        );

        // Group 1: Open/In Progress (NOT equal to "Completed" OR "Rejected")
        // This means: exclude only exact matches of "Completed" or "Rejected"
        const openProjects = validData.filter(item => {
          const category = item.projCategory.toLowerCase();
          return category !== 'completed' && category !== 'rejected';
        });

        // Group 2: Completed projects (exact match only)
        const completedProjects = validData.filter(item => 
          item.projCategory.toLowerCase() === 'completed'
        );

        // Calculate totals for each group
        const openEstimated = openProjects.reduce((sum, item) => sum + item.estimatedHours, 0);
        const openActual = openProjects.reduce((sum, item) => sum + item.actualHours, 0);
        const completedEstimated = completedProjects.reduce((sum, item) => sum + item.estimatedHours, 0);
        const completedActual = completedProjects.reduce((sum, item) => sum + item.actualHours, 0);

        return [
          {
            name: 'Not Completed',
            series: [
              {
                name: 'Estimated Hours',
                value: openEstimated
              },
              {
                name: 'Actual Hours',
                value: openActual
              }
            ]
          },
          {
            name: 'Completed',
            series: [
              {
                name: 'Estimated Hours',
                value: completedEstimated
              },
              {
                name: 'Actual Hours',
                value: completedActual
              }
            ]
          }
        ];
      })
    );
  }
}
